import { all, put, select, take, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import {
  fetchErknmAnalyticsProsecutorOrganization,
  fetchErknmAnalyticsProsecutorOrganizationExport,
  fetchErknmAnalyticsProsecutorOrganizationExportKnmList,
  fetchErknmAnalyticsProsecutorOrganizationFilter,
  fetchErknmAnalyticsProsecutorOrganizationKnm,
} from 'APIPackage/ErknmAnalyticsProsecutorOrganizationModule/services/erknmAnalyticsProsecutorOrganizationApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { notifyExportError, notifyExportSuccess } from 'CorePackage/MainModule/sagas/commonNotificationSagas';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { dictionariesKnmKindsItemGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { catalogGet, catalogGetSuccess } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { IErknmAnalyticsProsecutorOrganizationFilterRaw } from 'APIPackage/ErknmAnalyticsProsecutorOrganizationModule/interfaces/IErknmAnalyticsProsecutorOrganizationFilterRaw';
import {
  getCatalogsProsecutorOrganizationsSelectOptions,
  getCatalogsTerritorialUnitsSelectOptionsByLevelsCountryFederalDistrictRegions,
  getUserKnoSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { mapRawToData } from 'KnmListPackage/MainModule/common/mapRawToData';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IProsecutorOrganizationsSelectOption } from 'CorePackage/CatalogsModule/interfaces/IProsecutorOrganizationsSelectOption';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { CatalogsActionsEnum } from 'CorePackage/CatalogsModule/enums/CatalogsActionsEnum';
import { IErknmAnalyticsFilter } from 'AnalyticsPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsFilter';
import { IErknmAnalyticsData } from 'APIPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsData';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';

import {
  erknmAnalyticsProsecutorOrganizationExport,
  erknmAnalyticsProsecutorOrganizationExportKnmList,
  erknmAnalyticsProsecutorOrganizationGetKnmList,
  erknmAnalyticsProsecutorOrganizationGetKnmListNextPage,
  erknmAnalyticsProsecutorOrganizationSetData,
  erknmAnalyticsProsecutorOrganizationSetFilter,
  erknmAnalyticsProsecutorOrganizationSetKnmList,
  erknmAnalyticsProsecutorOrganizationSetTableColumns,
} from '../actions/erknmAnalyticsProsecutorOrganizationActions';
import { ErknmAnalyticsProsecutorOrganizationActionsEnum } from '../enums/ErknmAnalyticsProsecutorOrganizationActionsEnum';
import {
  getErknmAnalyticsProsecutorOrganizationFilter,
  getErknmAnalyticsProsecutorOrganizationKnmKindSelectOptions,
  getErknmAnalyticsProsecutorOrganizationKnmOrder,
  getErknmAnalyticsProsecutorOrganizationTableColumns,
} from '../selectors/erknmAnalyticsProsecutorOrganizationSelectors';
import { convertColumnsDataToTableColumns } from '../common/convertColumnsDataToTableColumns';
import { initialErknmAnalyticsProsecutorOrganizationTableColumns } from '../contstants/initialErknmAnalyticsProsecutorOrganizationTableColumns';

export function* erknmAnalyticsProsecutorOrganizationSaga() {
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.INIT_PAGE, erknmAnalyticsInitPageSaga);
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.GET_DATA, erknmAnalyticsGetDataSaga);
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.GET_DATA_NEXT, erknmAnalyticsGetDataNextSaga);
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.EXPORT, erknmAnalyticsExportSaga);
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.EXPORT_KNM_LIST, erknmAnalyticsExportKnmListSaga);
  yield takeEvery(ErknmAnalyticsProsecutorOrganizationActionsEnum.GET_KNM_LIST, erknmAnalyticsGetKnmListSaga);
  yield takeEvery(
    ErknmAnalyticsProsecutorOrganizationActionsEnum.GET_KNM_LIST_NEXT_PAGE,
    erknmAnalyticsGetKnmListNextPageSaga,
  );
}

const erknmAnalyticsPageCatalogs: CommonCatalogsNamesEnum[] = [
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS_BY_LEVELS_COUNTRY_FEDERAL_DISTRICTS_REGIONS,
  CommonCatalogsNamesEnum.PROSECUTOR_ORGANIZATIONS,
  CommonCatalogsNamesEnum.USER_KNOS,
];

function* waitForCatalogs() {
  // Создаем массив из эффектов `take` для каждого каталога
  const successActions = erknmAnalyticsPageCatalogs.map(catalogName =>
    take(
      (action: ActionType<typeof catalogGetSuccess>) =>
        action.type === CatalogsActionsEnum.CATALOG_GET_SUCCESS && action.payload.catalogName === catalogName,
    ),
  );

  // Ждем, пока все actions не завершатся
  yield all(successActions);
}

function* erknmAnalyticsInitPageSaga() {
  yield put(dictionariesKnmKindsItemGet());
  yield all(erknmAnalyticsPageCatalogs.map(catalogName => put(catalogGet(catalogName))));
  yield waitForCatalogs();
  yield erknmAnalyticsLoadFiltersSaga();
}

function* erknmAnalyticsLoadFiltersSaga() {
  const state: IRootState = yield select();
  let filter: IErknmAnalyticsFilter | undefined = getErknmAnalyticsProsecutorOrganizationFilter(state);

  if (!filter) {
    const filterData: IErknmAnalyticsProsecutorOrganizationFilterRaw = yield fetchErknmAnalyticsProsecutorOrganizationFilter();
    const knmKindSelectOptions: Array<IDictionarySelectOption<IKnmKind>> =
      getErknmAnalyticsProsecutorOrganizationKnmKindSelectOptions(state) ?? [];
    const territorialUnitsSelectOptions: Array<ITerritorialUnitSelectOption> = getCatalogsTerritorialUnitsSelectOptionsByLevelsCountryFederalDistrictRegions(
      state,
    );
    const prosecutorOrganizationsSelectOptions: Array<IProsecutorOrganizationsSelectOption> = getCatalogsProsecutorOrganizationsSelectOptions(
      state,
    );
    const knoSelectOptions: IUserOrganisationSelectOption[] = getUserKnoSelectOptions(state) ?? [];

    filter = getErknmAnalyticsProsecutorOrganizationFilter(state);

    filter = convertFilterRawToForm(
      filterData,
      knmKindSelectOptions,
      territorialUnitsSelectOptions,
      knoSelectOptions,
      prosecutorOrganizationsSelectOptions,
    );
    yield put(erknmAnalyticsProsecutorOrganizationSetFilter(filter));
  }

  return filter;
}

function* erknmAnalyticsGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const filter: IErknmAnalyticsFilter = yield erknmAnalyticsLoadFiltersSaga();
    const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
      ? convertFilterFormToRaw(filter)
      : undefined;
    const res: IErknmAnalyticsData = yield fetchErknmAnalyticsProsecutorOrganization(0, rawFilter);

    yield put(erknmAnalyticsProsecutorOrganizationSetData(res.rows, res.page, true, res.localDateTime));
    const columns = getErknmAnalyticsProsecutorOrganizationTableColumns(state);

    if (columns === initialErknmAnalyticsProsecutorOrganizationTableColumns) {
      yield put(
        erknmAnalyticsProsecutorOrganizationSetTableColumns(
          convertColumnsDataToTableColumns(res.columns) ?? initialErknmAnalyticsProsecutorOrganizationTableColumns,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* erknmAnalyticsGetDataNextSaga() {
  const state: IRootState = yield select();

  function* funcTry() {
    const filter: IErknmAnalyticsFilter | undefined = getErknmAnalyticsProsecutorOrganizationFilter(state);

    const analytics = state.erknmAnalyticsProsecutorOrganization;

    if (filter && analytics.hasMoreData) {
      const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
        ? convertFilterFormToRaw(filter)
        : undefined;

      const res: IErknmAnalyticsData = yield fetchErknmAnalyticsProsecutorOrganization(analytics.page, rawFilter);

      yield put(
        erknmAnalyticsProsecutorOrganizationSetData(
          [...analytics.data, ...res.rows],
          res.page,
          res.rows.length > 0,
          res.localDateTime,
        ),
      );
    }
  }

  const isLoading = getCoreIsLoading(state);

  if (!isLoading) {
    yield sagaWrapper({
      funcTry,
    });
  }
}

function flattenCodes(nodes: { id: string; inner: string[] }[]): string[] {
  const result: string[] = [];

  function traverse(node: { id: string; inner: string[] }) {
    result.push(node.id);

    if (node.inner && node.inner.length > 0) {
      result.push(...(node.inner as string[]));
    }
  }

  nodes.forEach(node => traverse(node));

  return result;
}

function* erknmAnalyticsExportSaga(action: ActionType<typeof erknmAnalyticsProsecutorOrganizationExport>) {
  function* funcTry() {
    const { format } = action.payload;
    const state: IRootState = yield select();
    const filter: IErknmAnalyticsFilter | undefined = yield select(getErknmAnalyticsProsecutorOrganizationFilter);
    const tableColumns = getErknmAnalyticsProsecutorOrganizationTableColumns(state);
    const columnsToSend: { id: string; inner: string[] }[] = tableColumns
      .filter(item => !item.mainId && item.id !== 'name')
      .map(i => {
        const additionalTableColumns = tableColumns.filter(item => item.mainId === i.id && item.isActive);

        return { id: i.id, inner: additionalTableColumns.map(j => j.id) };
      });

    const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
      ? convertFilterFormToRaw(filter)
      : undefined;
    const meta: IExport = yield fetchErknmAnalyticsProsecutorOrganizationExport(
      format,
      rawFilter,
      flattenCodes(columnsToSend),
    );

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

const convertFilterRawToForm = (
  filterData: IErknmAnalyticsProsecutorOrganizationFilterRaw,
  knmKindOptions: Array<IDictionarySelectOption<IKnmKind>>,
  territorialUnitsSelectOptions: Array<ITerritorialUnitSelectOption>,
  knoSelectOptions: IUserOrganisationSelectOption[],
  prosecutorOrganizationsSelectOptions: Array<IProsecutorOrganizationsSelectOption>,
): IErknmAnalyticsFilter => {
  return {
    ...filterData,
    districts: territorialUnitsSelectOptions.filter(
      item => filterData.districts.find(id => id === item.id.toString()) !== undefined,
    ),
    prosecutorOrganizations: prosecutorOrganizationsSelectOptions.filter(
      item => filterData.ervkProsecutorOrganizationIds?.find(id => id === item.id) !== undefined,
    ),
    knmKind: knmKindOptions.find(item => item.code === filterData.knmKind) ?? null,
    kno: (() => {
      const result = knoSelectOptions.filter(
        item => filterData.knoIds.find(id => String(id) === item.id) !== undefined,
      );

      const converted: IDictionarySelectOption<IKnoOrganizationDictionary>[] = result.map(item => {
        const convert: IDictionarySelectOption<IKnoOrganizationDictionary> = {
          id: item.id,
          ssn: null,
          guid: null,
          ogrn: item.ogrn || null,
          type: null,
          phone: null,
          address: null,
          enabled: null,
          fullName: item.value,
          parentId: null,
          shortName: null,
          adminLevel: null,
          dateCreate: null,
          code: null,
          dictVersionId: item.ervkVersionUuid,
          title: null,
          value: {
            dictId: (item.ervkUuid as DictionaryUUIDsEnum) || DictionaryUUIDsEnum.ERVK,
            dictVersionId: item.ervkVersionUuid || '',
          },
          label: item.label,
        };

        return convert;
      });

      return converted;
    })(),
    startDate: filterData.startDate
      ? formatDateLocale({
          date: filterData.startDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined,
    stopDate: filterData.stopDate
      ? formatDateLocale({
          date: filterData.stopDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined,
  };
};

function convertDistrictsIdsToStringIds(districts: ISelectOption<number>[] | undefined) {
  return districts?.map(item => item.id.toString()) ?? [];
}

function convertFilterFormToRaw(filter: IErknmAnalyticsFilter): IErknmAnalyticsProsecutorOrganizationFilterRaw {
  const {
    id,
    name,
    districts,
    prosecutorOrganizations,
    knmKind,
    kno,
    startDate,
    stopDate,
    withInnerDistricts: isInnerDistricts,
    withTerritoryAgencies: isTerritoryAgencies,
    withSubProsecutorOrganizations: isSubProsecutorOrganizations,
  } = filter;
  const urlParams = new URLSearchParams(window.location.search);
  const withTrace = urlParams.get('withTrace');
  const version = Number(urlParams.get('version'));

  return {
    id: id ?? null,
    name: name ?? null,
    knoIds: kno?.map(item => item.id) ?? [],
    districts: convertDistrictsIdsToStringIds(districts),
    ervkProsecutorOrganizationIds: prosecutorOrganizations?.map<string>(item => item.id) ?? [],
    knmKind: knmKind?.code ?? null,
    startDate: startDate
      ? formatDate({
          date: startDate,
          dateFormat: SYSTEM_DATE_FORMAT_FNS,
        })
      : null,
    stopDate: stopDate
      ? formatDate({
          date: stopDate,
          dateFormat: SYSTEM_DATE_FORMAT_FNS,
        })
      : null,
    withInnerDistricts: isInnerDistricts,
    withTerritoryAgencies: isTerritoryAgencies,
    withSubProsecutorOrganizations: isSubProsecutorOrganizations,
    ...(withTrace && { withTrace: withTrace === 'true' }),
    ...(version && { version }),
  };
}

function* erknmAnalyticsExportKnmListSaga(
  action: ActionType<typeof erknmAnalyticsProsecutorOrganizationExportKnmList>,
) {
  function* funcTry() {
    const { r, c, f, filter, format } = action.payload;
    const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
      ? convertFilterFormToRaw(filter)
      : undefined;
    const meta: IExport = yield fetchErknmAnalyticsProsecutorOrganizationExportKnmList(r, c, f, format, rawFilter);

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* erknmAnalyticsGetKnmListSaga(action: ActionType<typeof erknmAnalyticsProsecutorOrganizationGetKnmList>) {
  function* funcTry() {
    const { f, filter } = action.payload;
    const knmOrder: string | null = yield select(getErknmAnalyticsProsecutorOrganizationKnmOrder);
    const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
      ? convertFilterFormToRaw(filter)
      : undefined;
    const res: IKnmIndexPage = yield fetchErknmAnalyticsProsecutorOrganizationKnm(f, 0, 20, rawFilter, knmOrder);

    yield put(
      erknmAnalyticsProsecutorOrganizationSetKnmList(
        mapRawToData(res.list, true),
        res.page,
        res.pageCount,
        res.totalCount,
      ),
    );
  }
  yield sagaWrapper({
    funcTry,
  });
}

function* erknmAnalyticsGetKnmListNextPageSaga(
  action: ActionType<typeof erknmAnalyticsProsecutorOrganizationGetKnmListNextPage>,
) {
  function* funcTry() {
    const { f, filter } = action.payload;
    const state: IRootState = yield select();
    const analytics = state.erknmAnalyticsProsecutorOrganization;

    const rawFilter: IErknmAnalyticsProsecutorOrganizationFilterRaw | undefined = filter
      ? convertFilterFormToRaw(filter)
      : undefined;
    const res: IKnmIndexPage = yield fetchErknmAnalyticsProsecutorOrganizationKnm(
      f,
      analytics.knmPage + 1,
      20,
      rawFilter,
      analytics.knmOrder,
    );

    yield put(
      erknmAnalyticsProsecutorOrganizationSetKnmList(
        [...analytics.knmList, ...mapRawToData(res.list, true)],
        res.page,
        res.pageCount,
        res.totalCount,
      ),
    );
  }
  yield sagaWrapper({
    funcTry,
  });
}
